"use client";

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { isAuthenticated, signOut, getAuthToken } from '@/lib/auth';
import { configureAmplify } from '@/lib/amplify-config';

interface AuthContextType {
  isLoggedIn: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();

  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const authenticated = await isAuthenticated();
      setIsLoggedIn(authenticated);
      return authenticated;
    } catch (error) {
      console.error('認証確認エラー:', error);
      setIsLoggedIn(false);
      return false;
    }
  }, []);

  const logout = async () => {
    await signOut();
    setIsLoggedIn(false);
  };

  const getToken = async (): Promise<string | null> => {
    return await getAuthToken();
  };

  useEffect(() => {
    // 認証状態を確認
    const initAuth = async () => {
      setIsLoading(true);
      // Amplify設定を初期化（Cognitoモードの場合のみ必要）。
      // 設定完了前に fetchAuthSession が走ると「未設定」で未認証扱いになるため、必ず await する。
      if (process.env.NEXT_PUBLIC_AUTH_MODE !== 'local') {
        await configureAmplify();
      }
      await checkAuth();
      setIsLoading(false);
    };

    initAuth();
  }, [checkAuth]);

  // パスが変更されたときに認証状態を再確認（ログインページを除く）
  useEffect(() => {
    if (!isLoading && !pathname.includes('/login')) {
      checkAuth();
    }
  }, [pathname, isLoading, checkAuth]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, isLoading, logout, checkAuth, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};
